import { DestroyRef, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  EMPTY,
  BehaviorSubject,
  ReplaySubject,
  Subject,
  catchError,
  from,
  merge,
  switchMap,
  takeWhile,
  timer,
} from 'rxjs';
import { IntegrationService } from '../../services/integration.service';
import {
  CrawlerLaunchEligibility,
  CrawlerStatus,
  ACTIVE_CRAWLER_STATUSES,
} from '../../models/integration/crawler';

export const POLL_INTERVAL_MS = 10_000;

export function isCrawlerActive(status?: CrawlerStatus): boolean {
  return status ? ACTIVE_CRAWLER_STATUSES.has(status) : false;
}

// Upper bound for the optimistic window after a launch. The orchestrator sets
// CRAWLING/PENDING asynchronously (queue round-trip); if it never does (e.g. a
// dropped message) we stop polling after this delay instead of forever.
const LAUNCH_GRACE_MS = 5 * 60 * 1_000; // 5min


const CONFIRMED_RUNNING_STATUSES: ReadonlySet<CrawlerStatus> = new Set(
  Array.from(ACTIVE_CRAWLER_STATUSES).filter(s => s !== CrawlerStatus.PENDING),
);

function isConfirmedRunning(status?: CrawlerStatus): boolean {
  return status ? CONFIRMED_RUNNING_STATUSES.has(status) : false;
}

/**
 * Optimistic "launch pending" window. Right after a launch we don't know the
 * server state yet (the queue flips it asynchronously), so the poll keeps
 * running while this window is open even if the first response is still the
 * previous terminal status.
 */
export class LaunchPending {
  // Deadline as epoch ms; 0 = window closed. Kept reactive so the expiry can
  // be observed (and CD triggered) on time instead of lazily on next read.
  private readonly pendingUntil$ = new BehaviorSubject(0);

  // Fires exactly when an open window expires, so the poller can emit one
  // final settled election right at the deadline instead of up to a poll
  // interval later.
  readonly expiry$ = this.pendingUntil$.pipe(
    switchMap((until) => until > 0 ? timer(until - Date.now()) : EMPTY),
  );

  // Opens the window
  mark(): void {
    const until = Date.now() + LAUNCH_GRACE_MS;
    this.pendingUntil$.next(Math.max(this.pendingUntil$.value, until));
  }

  // Closes the window (e.g. server acknowledged an active status / cancel)
  clear(): void {
    this.pendingUntil$.next(0);
  }

  // True while the window is open
  get isPending(): boolean {
    return this.pendingUntil$.value > Date.now();
  }
}

/**
 * Polls launch eligibility while the crawler is active (or a launch is
 * pending), then stops on the first settled state
 */
export class LaunchEligibilityPoller {
  eligibility?: CrawlerLaunchEligibility;
  readonly launchPending = new LaunchPending();
  private readonly integrationId$ = new Subject<string>();
  // Emits each settled eligibility and replays the latest to subscribers that
  // arrive later (the poll stops after a settled inactive state, so a fresh
  // consumer would otherwise miss the last value forever).
  readonly eligibility$ = new ReplaySubject<CrawlerLaunchEligibility>(1);

  constructor(
    integrationService: IntegrationService,
    destroyRef: DestroyRef,
    private readonly zone: NgZone,
    onSettle?: (eligibility: CrawlerLaunchEligibility) => void,
  ) {
    // poll on each emitted id; keep running while active or pending
    this.integrationId$.pipe(
      switchMap((integrationId) =>
        merge(
          timer(0, POLL_INTERVAL_MS),
          // Trigger one final fetch+emit exactly when the pending window
          // expires, so CD runs on time rather than on the next tick.
          this.launchPending.expiry$,
        ).pipe(
          switchMap(() => from(
            integrationService.retrieveLaunchEligibility(integrationId),
          ).pipe(catchError(() => EMPTY))),
          takeWhile(
            (eligibility) =>
              isCrawlerActive(eligibility.status) ||
              this.launchPending.isPending,
            true,
          ),
        )),
      takeUntilDestroyed(destroyRef),
    // Emissions are brought back into the Angular zone: one arriving outside it (shared frame loops of the
    // chart and animation libraries) updated the status, buttons and lists without refreshing the screen.
    ).subscribe((eligibility) => this.zone.run(() => {
      this.eligibility = eligibility;
      if (isConfirmedRunning(eligibility.status)) {
        this.launchPending.clear();
      }
      this.eligibility$.next(eligibility);
      onSettle?.(eligibility);
    }));
  }

  // Starts (or restarts) polling for the given integration id
  start(integrationId: string): void {
    this.integrationId$.next(integrationId);
  }
}
